import { amountWithCurrencySymbol, CurrencyCode } from '../../helpers/currency';
import { Locale } from '../../helpers/languages';
import { USER_GROUP } from '../../helpers/user';
import { User, UserStatus } from '@/api/auth';
import { Bonus } from '@/api/common';
import { STORAGE, useCommonStore, useLocalStorageStore } from '@/store';

export enum BonanzaPromoStatus {
  LEVEL_0 = '0%',
  LEVEL_1 = '25%',
  LEVEL_2 = '50%',
  LEVEL_3 = '75%',
  LEVEL_4 = '100%',
  // tech status, not a user group
  BONUS_ISSUED = 'Bonus_Issued',
  PROMO_LOCKED = 'Used_Bonus',
}

const BONANZA_PROMO_ID = 'Bonanza';

export const getUserBonanzaPromoGroup = (statuses?: UserStatus[]) => {
  if (!statuses) return BonanzaPromoStatus.LEVEL_0;

  const bonusGroups = statuses.filter(
    ({ id }) => id.includes(BONANZA_PROMO_ID) && id.includes('%')
  );

  if (!bonusGroups.length) return BonanzaPromoStatus.LEVEL_0;

  const mainGroup =
    bonusGroups.find(({ id }) =>
      id.includes(BonanzaPromoStatus.PROMO_LOCKED)
    ) || bonusGroups[0];

  const bonusGroupId = mainGroup.id;

  const bonusGroupPercent = bonusGroupId.split('_').slice(-1)[0];

  const isLevel4 = bonusGroupPercent === BonanzaPromoStatus.LEVEL_4;

  const isBonusBonanzaCLickStorage = useLocalStorageStore
    .getState()
    .get(STORAGE.BONUS_BONANZA_CLICK);

  if (isLevel4 && isBonusBonanzaCLickStorage)
    return BonanzaPromoStatus.PROMO_LOCKED;

  if (isLevel4 && !isBonusBonanzaCLickStorage)
    return BonanzaPromoStatus.BONUS_ISSUED;

  if (!isLevel4 && isBonusBonanzaCLickStorage) {
    useLocalStorageStore.getState().remove(STORAGE.BONUS_BONANZA_CLICK);
  }

  return bonusGroupPercent as BonanzaPromoStatus;
};

export const userRewardCurrencyMap: Record<string, CurrencyCode> = {
  [Locale.ENGLISH]: CurrencyCode.USD,
  [Locale.ENGLISH_AUSTRALIA]: CurrencyCode.AUD,
  [Locale.ENGLISH_CANADA]: CurrencyCode.CAD,
  [Locale.GERMAN]: CurrencyCode.EUR,
  [Locale.FRENCH_CANADA]: CurrencyCode.CAD,
  [Locale.FRENCH_SWITZERLAND]: CurrencyCode.EUR,
  [Locale.ENGLISH_IRELAND]: CurrencyCode.EUR,
  [Locale.NORWEGIAN]: CurrencyCode.NOK,
  [Locale.ENGLISH_NEW_ZEALAND]: CurrencyCode.NZD,
};

const commonRewardMap: Record<string, number> = {
  [USER_GROUP.Loyals]: 25,
  [USER_GROUP.Champions]: 35,
  [USER_GROUP.GOLD]: 50,
  [USER_GROUP.PLATINUM]: 50,
  [USER_GROUP.DIAMOND]: 50,
};

const norwayRewardMap: Record<string, number> = {
  [USER_GROUP.Loyals]: 250,
  [USER_GROUP.Champions]: 350,
  [USER_GROUP.GOLD]: 500,
  [USER_GROUP.PLATINUM]: 500,
  [USER_GROUP.DIAMOND]: 500,
};

const bonanzaPromoGroups = [
  USER_GROUP.Loyals,
  USER_GROUP.Champions,
  USER_GROUP.GOLD,
  USER_GROUP.PLATINUM,
  USER_GROUP.DIAMOND,
];

export const bonanzaPromoProgressPercent = {
  [BonanzaPromoStatus.LEVEL_0]: 0,
  [BonanzaPromoStatus.LEVEL_1]: 25,
  [BonanzaPromoStatus.LEVEL_2]: 50,
  [BonanzaPromoStatus.LEVEL_3]: 75,
  [BonanzaPromoStatus.LEVEL_4]: 100,
  [BonanzaPromoStatus.BONUS_ISSUED]: 100,
  [BonanzaPromoStatus.PROMO_LOCKED]: 100,
};

const findBonanzaPromoGroup = (user?: User) => {
  if (!user?.statuses) return;

  return user.statuses.find(({ id }) =>
    bonanzaPromoGroups.includes(id as USER_GROUP)
  )?.id;
};

export const getUserReward = (locale: string, user?: User) => {
  const currency = userRewardCurrencyMap[locale];

  const userGroupId = findBonanzaPromoGroup(user) || USER_GROUP.Loyals;

  if (locale === Locale.NORWEGIAN) {
    return { reward: norwayRewardMap[userGroupId], currency };
  }

  return { reward: commonRewardMap[userGroupId], currency };
};

export const getBonanzaPromoBonus = (bonuses: Bonus[]) => {
  return bonuses.find(({ title }) => title.includes(BONANZA_PROMO_ID));
};

export const updateBonanzaData = (language: string, user?: User) => {
  const userBonanzaGroup = getUserBonanzaPromoGroup(user?.statuses);

  const bonanzaProgressPercent = bonanzaPromoProgressPercent[userBonanzaGroup];

  const userReward = getUserReward(language, user);

  const userBonanzaGroupKey = Object.entries(BonanzaPromoStatus).find(
    ([, value]) => value === userBonanzaGroup
  )?.[0];

  useCommonStore.setState({
    userBonanzaGroup,
    userBonanzaGroupKey,
    bonanzaProgressPercent,
    bonanzaReward: amountWithCurrencySymbol(
      userReward.currency,
      userReward.reward
    ),
  });
};
